import React from 'react';
import { isNil } from 'lodash';
import { IChartSettings, IParsedChartDataRecord } from '../interfaces';
import { MaybeNil } from '../../../utils/interfaces';
import { IFunnelLabelBar } from './types';
import { isNotNil } from '../../../utils/types';
import { getValueFromDataPoint } from '../../../utils/redux/util';
import { formatPercentTiny, formatValueTiny } from '../../../utils/formatText';
import { ICustomBarLabel } from '../CustomBarLabel/types';
import { CustomBarLabel } from '../CustomBarLabel/CustomBarLabel';
import { arrangeFunnelLabels } from '../CustomBarLabel/arrangeLabels';

const MIN_BAR_WIDTH = 30;

const getCommonBarProps = (
  bars: Array<MaybeNil<IFunnelLabelBar>>,
): MaybeNil<IFunnelLabelBar> => {
  const availableDims = bars.filter(isNotNil);
  return availableDims.length > 0 ? availableDims[0] : null;
};

interface IProps {
  conversionBar?: IFunnelLabelBar;
  bounceBar?: IFunnelLabelBar;
  data: IParsedChartDataRecord[];
  chartSettings: IChartSettings;
}

export const CustomFunnelLabel = ({
  conversionBar,
  bounceBar,
  data,
  chartSettings,
}: IProps) => {
  const commonBarProps = getCommonBarProps([conversionBar, bounceBar]);

  if (isNil(commonBarProps)) {
    return null;
  }
  const {
    bar: { width: barWidth },
    index,
    dataKeys,
    color,
  } = commonBarProps;
  if (barWidth < MIN_BAR_WIDTH) {
    return null;
  }

  const fillColor =
    (isNil(color) ? data?.[index]?.fill?.toString() : color) ?? '#000';
  const {
    isFunnelRelativeLabels,
    isShowFunnelBounceLabels,
    isShowFunnelConversionLabels,
  } = chartSettings;
  const dataPoint = data[index];
  const value = getValueFromDataPoint(dataPoint, dataKeys.valueKey);
  const bounceKey = chartSettings.isFunnelRelativeLabels
    ? dataKeys.relativeBounceKey
    : dataKeys.bounceValueKey;
  const bounceValue = getValueFromDataPoint(dataPoint, bounceKey);

  const conversionLabels: ICustomBarLabel[] = [];
  if (
    isShowFunnelConversionLabels &&
    !isNil(conversionBar) &&
    !isNil(value) &&
    value !== 0
  ) {
    const percentKey = isFunnelRelativeLabels
      ? dataKeys.relativePercentKey
      : dataKeys.percentKey;
    const percentage = getValueFromDataPoint(dataPoint, percentKey) ?? 0;
    const percentToDisplay = formatPercentTiny(percentage);

    conversionLabels.push({
      outsideBarColor: fillColor,
      insideBarColor: '#fff',
      value: percentToDisplay,
      svgProps: {
        fontSize: '11px',
        fontWeight: '600',
      },
    });

    const conversionToDisplay = formatValueTiny(value);
    conversionLabels.push({
      outsideBarColor: fillColor,
      insideBarColor: '#fff',
      value: conversionToDisplay,
      svgProps: {
        fontSize: '9px',
        fontWeight: '600',
      },
    });
  }

  const bounceLabels: ICustomBarLabel[] = [];
  if (
    isShowFunnelBounceLabels &&
    !isNil(bounceBar) &&
    !isNil(bounceValue) &&
    bounceValue !== 0
  ) {
    const bouncePercentKey = isFunnelRelativeLabels
      ? dataKeys.relativeBouncePercentKey
      : dataKeys.bouncePercentKey;
    const bouncePercent =
      getValueFromDataPoint(dataPoint, bouncePercentKey) ?? 0;
    const bouncePercentToDisplay = formatPercentTiny(bouncePercent);

    bounceLabels.push({
      outsideBarColor: '#888',
      insideBarColor: '#888',
      value: bouncePercentToDisplay,
      svgProps: {
        fontSize: '10px',
        fontStyle: 'italic',
        fontWeight: '500',
      },
    });

    const bounceValueToDisplay = formatValueTiny(bounceValue);

    bounceLabels.push({
      outsideBarColor: '#888',
      insideBarColor: '#888',
      value: bounceValueToDisplay,
      svgProps: {
        fontSize: '9px',
        fontStyle: 'italic',
        fontWeight: '500',
      },
    });
  }

  const positionedLabels = arrangeFunnelLabels(
    conversionBar?.bar,
    conversionLabels,
    bounceBar?.bar,
    bounceLabels,
  );

  return (
    <g>
      {positionedLabels.map((pl, i) => (
        <CustomBarLabel key={i} label={pl} />
      ))}
    </g>
  );
};
